import React from "react";
import AnswerText from "./answers/AnswerText";
import AnswerSelect from "./answers/AnswerSelect";
import AnswerMultiSelect from "./answers/AnswerMultiSelect";
import AnswerNumber from "./answers/AnswerNumber";
import AnswerDate from "./answers/AnswerDate";
import AnswerTable from "./answers/AnswerTable";
import AnswerFile from "./answers/AnswerFile";
import AnswerInstructions from "./answers/AnswerInstructions";
import AnswerSlots from "./answers/AnswerSlots";
import QuestionCitations from "./QuestionCitations";
import QuestionInstructionBlock from "./QuestionInstructionBlock";
import { RiskCriticalityBar, RiskFindingFooter } from "./RiskPanelCard";
import { findingSeverityOf, isFindingSeverity } from "./findingSeverity";
import RegulatoryGapBlock from "./RegulatoryGapBlock";

const isInstructionsSchema = (schema) => {
  const s = (schema || "").toLowerCase();
  return s === "instructions" || s === "instruction" || s === "info";
};

const stripCodePrefix = (text, code) => {
  if (!text) return "";
  if (!code) return text;
  const escaped = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.replace(new RegExp(`^\\s*${escaped}\\s*:\\s*`), "");
};

const renderAnswer = (q) => {
  const schema = (q.schema || "").toLowerCase();
  switch (schema) {
    case "select":
      return <AnswerSelect value={q.primary_answer} />;
    case "multi_select":
    case "multiselect":
      return <AnswerMultiSelect values={q.multi_answers ?? (q.primary_answer ? [q.primary_answer] : [])} />;
    case "number":
      return <AnswerNumber value={q.primary_answer} />;
    case "date":
      return <AnswerDate value={q.primary_answer} />;
    case "table":
      return <AnswerTable table={q.table} />;
    case "file":
      return <AnswerFile attachments={q.attachments} />;
    case "text":
    case "textarea":
    default:
      return <AnswerText value={q.primary_answer} />;
  }
};

/**
 * 14030 — Question card on the Questionnaire & Risk Analysis tab.
 *
 * Layout (matches the design):
 *   [Code]   Question text
 *            QUESTION CRITICALITY  · I/L/D · Risk: 18 · Major
 *            Yes/No/NA*
 *            [Radio cards]
 *            Comments
 *            [Comment textarea]
 *            Supporting Documents
 *            [File rows]
 *           ╭ RISK FINDING · Compliant · Auto-scored · Reviewer · Timestamp ╮
 *           ╰─────────────────────────────────────────────────────────────╯
 */
const VqqQuestionRenderer = ({
  question,
  scoredRow,
  methodology,
  isAdmin = false,
  mode = null,
  missingGapTextIds = null,
  onSmeSave = null,
}) => {
  if (!question) return null;
  const code = question.question_code;
  const cleanText = stripCodePrefix(question.question_text || "", code);
  const schemaLower = (question.schema || "").toLowerCase();
  const isSelectSchema = schemaLower === "select" || (question.answer_type ?? "").toLowerCase().includes("select");
  // Custom templates carry their full labeled field set in answer_slots; Diligent
  // standard questions ("standard" layout) keep the historic Yes/No/NA + Comments +
  // Supporting Documents shape. Tables keep their grid renderer either way.
  const isCustomSlots =
    (question.answer_layout || "standard") === "custom" &&
    Array.isArray(question.answer_slots) &&
    question.answer_slots.length > 0 &&
    schemaLower !== "table";

  if (isInstructionsSchema(question.schema)) {
    return (
      <article className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex gap-4">
          <div className="shrink-0">
            {code ? <CodeChip code={code} /> : null}
          </div>
          <div className="flex-1 min-w-0 space-y-2">
            <AnswerInstructions text={question.question_text || ""} />
            <QuestionCitations citations={question.citations} />
          </div>
        </div>
      </article>
    );
  }

  // The gap block shows exactly when the Risk Finding pill shows a finding.
  const isNonCompliantFinding = scoredRow ? isFindingSeverity(findingSeverityOf(scoredRow)) : false;
  const gapRequired = (mode === "SmeOnly" || mode === "Both") && isNonCompliantFinding;
  const gapHighlighted =
    missingGapTextIds?.has?.(scoredRow?.rfi_question_scored_snapshot_id) ?? false;

  // onSmeSave surfaces its own NotificationModal on errors AND rethrows so
  // local UI state (gap textarea savedText, pill saving spinner) can react.
  // We catch here so the rejection doesn't bubble out of a React event
  // handler and trip react-error-overlay with "Uncaught runtime error".
  const handleSaveGap = async (nextText) => {
    if (!onSmeSave || !scoredRow) return;
    try {
      await onSmeSave({
        snapshotId: scoredRow.rfi_question_scored_snapshot_id,
        payload: {
          sme_tier: scoredRow.sme_tier || null,
          sme_review_notes: nextText,
        },
      });
    } catch {
      // Handled — modal already shown by onSmeSave.
    }
  };

  // SME-only severity override. Clicking the Risk Finding pill cycles to
  // the next tier; payload preserves any existing gap notes.
  const handleOverrideTier = async (nextTier) => {
    if (!onSmeSave || !scoredRow) return;
    try {
      await onSmeSave({
        snapshotId: scoredRow.rfi_question_scored_snapshot_id,
        payload: {
          sme_tier: nextTier,
          sme_review_notes: scoredRow.sme_review_notes || null,
        },
      });
    } catch {
      // Handled — modal already shown by onSmeSave.
    }
  };

  return (
    <article className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div className="flex gap-4">
        <div className="shrink-0">
          {code ? <CodeChip code={code} /> : null}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
            {code ? <span className="font-semibold mr-1">{code}:</span> : null}
            {cleanText}
          </p>

          <RiskCriticalityBar scoredRow={scoredRow} methodology={methodology} />

          <QuestionInstructionBlock instructions={question.instructions} />

          {isCustomSlots ? (
            // Custom templates: every labeled answer field the template defines, with
            // that field's own options and value. The slots include the Comments and
            // Supporting Documents fields, so the standard blocks below are skipped.
            <AnswerSlots slots={question.answer_slots} attachments={question.attachments} />
          ) : (
            <>
              <div>
                {!isSelectSchema ? (
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Response</div>
                ) : null}
                {renderAnswer(question)}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Comments</div>
                {question.comments ? (
                  <textarea
                    readOnly
                    value={question.comments}
                    rows={Math.min(6, Math.max(2, question.comments.split("\n").length + 1))}
                    className="w-full text-sm text-gray-800 bg-white border border-gray-300 rounded-md p-3 focus:outline-none resize-none"
                  />
                ) : (
                  <p className="text-sm italic text-gray-400">No comments</p>
                )}
              </div>

              {schemaLower !== "file" ? (
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Supporting documents</div>
                  <AnswerFile attachments={question.attachments} />
                </div>
              ) : null}
            </>
          )}

          <QuestionCitations citations={question.citations} />
        </div>
      </div>

      <RiskFindingFooter
        scoredRow={scoredRow}
        canOverride={isAdmin}
        onOverrideTier={handleOverrideTier}
      />

      {isNonCompliantFinding ? (
        <RegulatoryGapBlock
          scoredRow={scoredRow}
          initialGapText={scoredRow?.sme_review_notes || scoredRow?.gap_text || ""}
          citations={Array.isArray(question.citations) ? question.citations : []}
          isAdmin={isAdmin}
          isRequired={gapRequired}
          isHighlighted={gapHighlighted}
          onSaveGap={isAdmin ? handleSaveGap : null}
        />
      ) : null}
    </article>
  );
};

const CodeChip = ({ code }) => (
  <span className="inline-flex items-center justify-center min-w-[3.25rem] h-7 px-2 rounded text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
    {code}
  </span>
);

export default VqqQuestionRenderer;
